/**
 * Odds Movement Tab Component
 * Inline version for the odds modal tab
 */

import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Activity, AlertTriangle, BarChart3, Eye } from 'lucide-react';
import oddsTracker, { extractH2HOdds } from '../rtdb/oddsTracker';
import { rtdbFootballService } from '../rtdb';

const OddsMovementTab = ({ fixture }) => {
  const [history, setHistory] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveMovement, setLiveMovement] = useState(null);

  useEffect(() => {
    if (!fixture?.fixture?.id) return;

    const loadData = async () => {
      setLoading(true);
      try {
        // First, fetch current odds to establish baseline
        const currentOdds = await rtdbFootballService.getOdds(fixture.fixture.id);
        
        // Then get historical data
        const [historyData, movementsData] = await Promise.all([
          oddsTracker.getOddsHistory(fixture.fixture.id),
          oddsTracker.getMovements(fixture.fixture.id)
        ]);
        
        // If we have current odds but no history, create initial entry
        if (currentOdds?.preMatch?.[0] && historyData.length === 0) {
          const initialOdds = extractH2HOdds(currentOdds.preMatch);
          if (initialOdds) {
            const initialEntry = {
              timestamp: Date.now(),
              ...initialOdds
            };
            setHistory([initialEntry]);
          }
        } else {
          setHistory(historyData);
        }
        
        setMovements(movementsData);
        
        // Start tracking this fixture for future changes
        if (fixture.league?.id) {
          oddsTracker.trackFixture(fixture.fixture.id, fixture.league.id);
        }
      } catch (error) {
        console.error('Failed to load odds data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Listen for live movements
    const handleMovement = (event) => {
      if (event.detail.fixtureId === fixture.fixture.id) {
        setLiveMovement(event.detail.movement);
        setTimeout(() => setLiveMovement(null), 5000);
        oddsTracker.getMovements(fixture.fixture.id).then(setMovements);
      }
    };

    window.addEventListener('oddsMovement', handleMovement);
    return () => window.removeEventListener('oddsMovement', handleMovement);
  }, [fixture]);

  const formatChange = (value) => {
    if (!value || value === 0) return '0%';
    const formatted = value.toFixed(1);
    return value > 0 ? `+${formatted}%` : `${formatted}%`;
  };

  const getTrendIcon = (change) => {
    if (change > 0) return <TrendingUp className="text-red-400" size={16} />;
    if (change < 0) return <TrendingDown className="text-green-400" size={16} />;
    return null;
  };

  const formatOdds = (odds) => odds ? odds.toFixed(2) : '-';

  const getImpliedProbability = (odds) => {
    if (!odds) return 0;
    return ((1 / odds) * 100).toFixed(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Live Alert */}
      {liveMovement && (
        <div className="bg-yellow-500/20 border-l-4 border-yellow-500 p-4 rounded-lg animate-pulse">
          <div className="flex items-center gap-3">
            <AlertTriangle className="text-yellow-400" size={24} />
            <div>
              <p className="text-yellow-400 font-semibold">Line Movement Alert!</p>
              <div className="flex gap-4 text-sm text-white/80 mt-1">
                <span>Home: {formatChange(liveMovement.home)}</span>
                <span>Draw: {formatChange(liveMovement.draw)}</span>
                <span>Away: {formatChange(liveMovement.away)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Current Odds with Movement */}
      {history.length > 0 && (
        <div>
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <BarChart3 size={20} />
            Current Lines & Movement
          </h3>
          <div className="grid grid-cols-3 gap-4">
            {['home', 'draw', 'away'].map((type) => {
              const current = history[history.length - 1];
              const previous = history[history.length - 2];
              const odds = current[`${type}Odds`];
              const change = previous ? 
                ((odds - previous[`${type}Odds`]) / previous[`${type}Odds`]) * 100 : 0;
              
              return (
                <div key={type} className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <p className="text-white/60 text-sm capitalize mb-2">{type}</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-white">
                      {formatOdds(odds)}
                    </span>
                    {change !== 0 && (
                      <span className={`text-sm flex items-center gap-1 ${
                        change > 0 ? 'text-red-400' : 'text-green-400'
                      }`}>
                        {getTrendIcon(change)}
                        {formatChange(change)}
                      </span>
                    )}
                  </div>
                  <p className="text-white/40 text-xs mt-2">
                    {getImpliedProbability(odds)}% implied
                  </p>
                </div>
              );
            })}
          </div>
          {history[history.length - 1]?.sampleSize && (
            <p className="text-white/40 text-xs mt-2 text-center">
              Average from {history[history.length - 1].sampleSize} bookmakers
            </p>
          )}
        </div>
      )}

      {/* Significant Movements */}
      {movements.length > 0 && (
        <div>
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Activity size={20} />
            Significant Line Movements
          </h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {movements.slice(0, 10).map((movement, idx) => (
              <div key={idx} className="bg-white/5 rounded-lg p-3 flex items-center justify-between">
                <span className="text-white/60 text-sm">
                  {new Date(movement.timestamp).toLocaleTimeString()}
                </span>
                <div className="flex gap-3 text-sm">
                  <span className={movement.home > 0 ? 'text-red-400' : 'text-green-400'}>
                    H: {formatChange(movement.home)}
                  </span>
                  <span className={movement.draw > 0 ? 'text-red-400' : 'text-green-400'}>
                    D: {formatChange(movement.draw)}
                  </span>
                  <span className={movement.away > 0 ? 'text-red-400' : 'text-green-400'}>
                    A: {formatChange(movement.away)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Historical Timeline */}
      {history.length > 1 && (
        <div>
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Eye size={20} />
            Line History (Last {Math.min(history.length, 8)} Updates)
          </h3>
          <div className="bg-white/5 rounded-lg p-4 overflow-x-auto">
            <div className="flex gap-4 min-w-max">
              {history.slice(-8).map((entry, idx) => (
                <div key={idx} className="text-center">
                  <p className="text-white/40 text-xs mb-2">
                    {new Date(entry.timestamp).toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                  <div className="space-y-1 text-sm">
                    <div className="text-emerald-400">{formatOdds(entry.homeOdds)}</div>
                    <div className="text-gray-400">{formatOdds(entry.drawOdds)}</div>
                    <div className="text-violet-400">{formatOdds(entry.awayOdds)}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-4 text-xs text-white/40 mt-3">
              <span><span className="inline-block w-2 h-2 bg-emerald-400 rounded-full mr-1"></span>Home</span>
              <span><span className="inline-block w-2 h-2 bg-gray-400 rounded-full mr-1"></span>Draw</span>
              <span><span className="inline-block w-2 h-2 bg-violet-400 rounded-full mr-1"></span>Away</span>
            </div>
          </div>
        </div>
      )}

      {/* No Data */}
      {history.length === 0 && (
        <div className="text-center py-8">
          <Activity className="text-white/20 mx-auto mb-3" size={48} />
          <p className="text-white/60">No line movement data available yet</p>
          <p className="text-white/40 text-sm mt-1">
            Odds are being monitored for changes
          </p>
        </div>
      )}
      
      {/* Single data point - waiting for movements */}
      {history.length === 1 && movements.length === 0 && (
        <div className="text-center py-4 bg-white/5 rounded-lg">
          <p className="text-white/60 text-sm">📊 Initial odds captured</p>
          <p className="text-white/40 text-xs mt-1">
            Waiting for line movements...
          </p>
        </div>
      )}

      {/* Disclaimer */}
      <div className="mt-6 p-3 bg-white/5 rounded-lg">
        <p className="text-white/40 text-xs">
          ⚠️ Line movements shown for informational purposes only. Data may be delayed up to 30 seconds.
        </p>
      </div>
    </div>
  );
};

export default OddsMovementTab;